import React, { useState } from "react";
import { Box, Chip, IconButton, Stack, Tooltip } from "@mui/material";
import {
  ChevronLeftOutlined,
  ChevronRightOutlined,
  StarOutlineOutlined,
} from "@mui/icons-material";
import PhotoImage from "./PhotoImage";
import { moveItem } from "../photoOrder";
import { radius, tokens } from "../theme/theme";

/**
 * A listing's photos, reorderable, shared by the Photos dialog and the Edit
 * dialog so the two cannot drift apart.
 *
 * Three ways to move a photo, because one is never enough:
 *   • drag a tile in front of another (a bar marks the gap it will land in) —
 *     what everyone reaches for with a mouse;
 *   • ‹ › to nudge it one place — precise, and the only thing that works on a
 *     touchscreen, where HTML5 drag events do not fire at all;
 *   • ★ to send it straight to first place, which is the move that actually
 *     matters: first photo is the cover, on the card and in every shared link.
 *
 * `actions` lets each caller add its own per-photo buttons (download, delete)
 * without this component knowing what they are.
 */
export default function PhotoGrid({
  photos,
  onReorder,
  onOpen,
  actions,
  columns,
  disabled,
}) {
  const [dragFrom, setDragFrom] = useState(-1);
  // The gap the dragged photo would land in: 0 is before the first photo,
  // `count` is after the last.
  const [dropSlot, setDropSlot] = useState(-1);

  const count = photos ? photos.length : 0;
  const canReorder = !!onReorder && !disabled && count > 1;

  const move = function (from, to) {
    if (!canReorder || to === from) return;
    onReorder(moveItem(photos, from, to));
  };

  const endDrag = function () {
    setDragFrom(-1);
    setDropSlot(-1);
  };

  // Hovering a photo means "before this one". The last photo's right half means
  // "after it" — otherwise the end of the list could not be reached by dragging.
  const slotAt = function (event, index) {
    if (index === count - 1) {
      const rect = event.currentTarget.getBoundingClientRect();
      if (event.clientX > rect.left + rect.width / 2) return count;
    }
    return index;
  };

  // The gaps either side of the dragged photo would leave it where it is.
  const isDropSlot = function (slot) {
    return (
      dragFrom >= 0 &&
      dropSlot === slot &&
      slot !== dragFrom &&
      slot !== dragFrom + 1
    );
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: columns || {
          xs: "repeat(2, minmax(0, 1fr))",
          sm: "repeat(3, minmax(0, 1fr))",
          md: "repeat(4, minmax(0, 1fr))",
        },
        gap: 1.5,
      }}
    >
      {(photos || []).map(function (item, index) {
        const isCover = index === 0;
        const dropBarSide = isDropSlot(index)
          ? "left"
          : index === count - 1 && isDropSlot(count)
          ? "right"
          : null;
        return (
          <Box
            key={item.key || item.name}
            draggable={canReorder}
            onDragStart={function (event) {
              if (!canReorder) return;
              setDragFrom(index);
              // Firefox ignores a drag that carries no data at all.
              if (event.dataTransfer) {
                event.dataTransfer.effectAllowed = "move";
                try {
                  event.dataTransfer.setData("text/plain", String(index));
                } catch (err) {
                  /* older browsers are fussy about setData; the drag still works */
                }
              }
            }}
            onDragOver={function (event) {
              if (!canReorder || dragFrom < 0) return;
              // Without preventDefault the browser refuses the drop outright.
              event.preventDefault();
              if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
              const slot = slotAt(event, index);
              if (dropSlot !== slot) setDropSlot(slot);
            }}
            onDrop={function (event) {
              if (!canReorder || dragFrom < 0) return;
              event.preventDefault();
              const slot = slotAt(event, index);
              // moveItem wants the index after removal, so every slot past the
              // dragged photo is one less.
              move(dragFrom, slot > dragFrom ? slot - 1 : slot);
              endDrag();
            }}
            onDragEnd={endDrag}
            sx={{
              position: "relative",
              borderRadius: radius.md,
              // No overflow: hidden — the drop bar sits outside the tile, in the
              // gap, so the image rounds its own corners instead.
              aspectRatio: "4 / 3",
              backgroundColor: tokens.line2,
              cursor: onOpen ? "pointer" : "default",
              opacity: dragFrom === index ? 0.35 : 1,
              transition: "opacity .12s ease",
              "&:hover .photo-actions": { opacity: 1 },
              // Touch has no hover, so the controls are always visible there.
              "@media (hover: none)": {
                "& .photo-actions": { opacity: 1 },
              },
            }}
            onClick={function () {
              if (onOpen) onOpen(index);
            }}
          >
            <PhotoImage
              src={item.thumbUrl}
              fallbackSrc={item.url}
              loading="lazy"
              alt={item.name}
              // A dragged <img> starts its own native image drag otherwise,
              // and the tile's drag never begins.
              draggable={false}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                borderRadius: radius.md,
                pointerEvents: "none",
              }}
            />

            {dropBarSide && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  // Centred in the grid gap (gap: 1.5, bar 4px wide).
                  [dropBarSide]: function (theme) {
                    return "calc(" + theme.spacing(-0.75) + " - 2px)";
                  },
                  width: "4px",
                  borderRadius: "2px",
                  backgroundColor: tokens.blue600,
                  pointerEvents: "none",
                }}
              />
            )}

            {isCover && (
              <Chip
                size="small"
                label="Головне"
                sx={{
                  position: "absolute",
                  top: 6,
                  left: 6,
                  backgroundColor: tokens.blue600,
                  color: "#fff",
                  fontWeight: 700,
                  pointerEvents: "none",
                }}
              />
            )}

            {actions && actions.length > 0 && (
              <Stack
                className="photo-actions"
                direction="row"
                spacing={0.5}
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  opacity: 0,
                  transition: "opacity .15s ease",
                }}
              >
                {actions.map(function (action) {
                  return (
                    <Tooltip key={action.key} title={action.label}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={action.isBusy ? action.isBusy(item) : false}
                          aria-label={action.label}
                          onClick={function (event) {
                            event.stopPropagation();
                            action.onClick(item, index);
                          }}
                          sx={action.sx}
                        >
                          {action.icon(item)}
                        </IconButton>
                      </span>
                    </Tooltip>
                  );
                })}
              </Stack>
            )}

            {canReorder && (
              <Stack
                className="photo-actions"
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{
                  position: "absolute",
                  bottom: 6,
                  left: 6,
                  right: 6,
                  opacity: 0,
                  transition: "opacity .15s ease",
                }}
              >
                <Tooltip title="Лівіше">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={"Лівіше: " + item.name}
                      disabled={index === 0}
                      onClick={function (event) {
                        event.stopPropagation();
                        move(index, index - 1);
                      }}
                      sx={reorderButtonSx}
                    >
                      <ChevronLeftOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Правіше">
                  <span>
                    <IconButton
                      size="small"
                      aria-label={"Правіше: " + item.name}
                      disabled={index === count - 1}
                      onClick={function (event) {
                        event.stopPropagation();
                        move(index, index + 1);
                      }}
                      sx={reorderButtonSx}
                    >
                      <ChevronRightOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Box sx={{ flexGrow: 1 }} />
                {!isCover && (
                  <Tooltip title="Зробити головним">
                    <IconButton
                      size="small"
                      aria-label={"Зробити головним: " + item.name}
                      onClick={function (event) {
                        event.stopPropagation();
                        move(index, 0);
                      }}
                      sx={reorderButtonSx}
                    >
                      <StarOutlineOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

const reorderButtonSx = {
  backgroundColor: "rgba(14,27,46,0.62)",
  color: "#fff",
  backdropFilter: "blur(4px)",
  "&:hover": { backgroundColor: "rgba(14,27,46,0.82)" },
  "&.Mui-disabled": { backgroundColor: "rgba(14,27,46,0.25)", color: "rgba(255,255,255,0.4)" },
};
